import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components/native';

import { memo } from '@webtrader/core/constants/react';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { isRTL } from '@webtrader/core/i18n';
import { useTheme } from '@webtrader/core/services/theme/v2';

import MaterialIcons from '@/components/Icons/Material';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';
import { goBack } from '@/utils/navigation';

interface INavigationHeaderProps {
  title?: ReactNode | string;
  suffix?: ReactNode;
  bordered?: boolean;
  autoPadding?: boolean;
  isBackButtonEnabled?: boolean;
  onBack?: () => void;
  titleTestId?: EDataTestId;
}

const NavigationHeader: FC<INavigationHeaderProps> = (props) => {
  const {
    title,
    suffix,
    bordered = false,
    autoPadding = false,
    isBackButtonEnabled = true,
    onBack,
    titleTestId
  } = props;

  const { theme } = useTheme();

  const headerTitle = useMemo(() => {
    if (typeof title === 'string') {
      return <HeaderTitleText>{title}</HeaderTitleText>;
    }

    return title;
  }, [title]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      goBack();
    }
  }, [onBack]);

  return (
    <Header bordered={bordered} autoPadding={autoPadding}>
      {isBackButtonEnabled && (
        <HeaderPrefix>
          <HeaderBackAction autoPadding={autoPadding} onPress={handleBack} testID={EDataTestId.NAVIGATION_BACK_BUTTON}>
            <MaterialIcons
              name={isRTL() ? 'arrow-forward-ios' : 'arrow-back-ios'}
              color={theme.colors.text[0]}
              size={20}
            />
          </HeaderBackAction>
        </HeaderPrefix>
      )}
      <HeaderTitle testID={titleTestId}>{headerTitle}</HeaderTitle>
      <HeaderSuffix>{suffix}</HeaderSuffix>
    </Header>
  );
};

export default memo(NavigationHeader);

export const Header = styled.View<{ bordered?: boolean; autoPadding?: boolean }>`
  height: ${({ theme }) => theme.sizes.mobile.navigation.headerHeight}px;

  align-items: center;
  flex-direction: row;
  width: 100%;

  border-bottom-width: ${({ bordered }) => (bordered ? 1 : 0)}px;
  border-bottom-color: ${({ theme }) => theme.colors.border[0]}px;

  ${({ theme, autoPadding }) =>
    autoPadding &&
    css`
      padding-end: ${theme.sizes.mobile.basePaddingEnd}px;
    `}
`;

export const HeaderPrefix = styled.View`
  flex: 1;
`;

export const HeaderBackAction = styled(TouchableOpacity)<{ autoPadding?: boolean }>`
  height: ${({ theme }) => theme.sizes.mobile.navigation.headerHeight}px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${() => isRTL() && 'flex-start'};

  padding-start: ${({ theme, autoPadding }) => (autoPadding ? theme.sizes.mobile.basePaddingStart : 0)}px;
  padding-end: ${({ theme }) => theme.sizes.mobile.basePaddingEnd}px;
`;

const HeaderTitle = styled.View``;

const HeaderTitleText = styled(Text)`
  font-size: ${dynamicSize.convertSize(16)}px;
  font-weight: 500;
`;

const HeaderSuffix = styled.View`
  flex: 1;
  align-items: flex-end;
`;
